/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import { configureStore, ThunkDispatch } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import reducers from './reducers'
import { loadState, saveState, throttle } from 'helpers/helpers'
import clearStoreOnUpdate from 'utils/clearStoreOnUpdate'
import { Action } from 'helpers/createAction'

import { IS_DEV, PROJECT_VERSION } from 'helpers/config'

if (!(window as any).Cypress) {
    clearStoreOnUpdate()
}

const persistedState = loadState()

export const store = configureStore({
    reducer: reducers,
    preloadedState: persistedState,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false,
        }),
    devTools: {
        name: 'CRMSeller Store',
        trace: true,
        traceLimit: 50,
    },
})

const throttledSaveState = throttle(() => {
    saveState(store.getState())
}, IS_DEV ? 100 : 1000)

store.subscribe(throttledSaveState)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export type AppThunkDispatch = ThunkDispatch<RootState, unknown, Action>

export const useAppDispatch = () => useDispatch<AppThunkDispatch>()

if ((window as any).Cypress) {
    ; (window as any).store = store
}

export default store
